import { HttpRequest } from "@/utils/request"

export type VideoStatus = "draft" | "published" | "archived"
export const videoStatuses = [
  { value: "draft", label: "Bản nháp" },
  { value: "published", label: "Đã xuất bản" },
  { value: "archived", label: "Đã lưu trữ" },
]
export interface Video {
  id: string
  name: string
  image: string | null
  duration: number
  level: string | null
  tags: string[] | null
  is_public: boolean
  views: string
  segment_count: number
  type: "youtube" | "audio"
  source_id: string
  status: VideoStatus
  created_by: number | null
  creator?: { id: number; full_name: string | null; username: string }
  created_at: string
  updated_at: string
}
export interface VideoSegment {
  id: number | string
  start: number
  end: number
  duration: number
  text: string
  translation: string | null
  words: {
    text: string
    start: number
    end: number
    duration: number
    startEstimated: boolean
    endEstimated: boolean
  }[]
}
export interface SubtitleDocument {
  schemaVersion: 2
  videoId: string
  language: string
  totalSegments: number
  sourceLanguage: string
  targetLanguage: "vi"
  source: {
    provider: "youtube"
    sourceId: string
    captionKind: "manual" | "automatic"
    trackLanguage: string
    rawPath: string
  }
  segments: VideoSegment[]
}
export interface SubtitleResult {
  document: SubtitleDocument
  revision: string
  total: number
  translated: number
  remaining: number
}
interface ApiResult<T> {
  success: boolean
  code: number
  message: string
  data: T
}
export type VideoMetadata = Pick<Video, "name" | "image" | "duration" | "is_public">
export interface VideoFilters {
  page: number
  limit?: number
  per_page?: number
  search?: string
  status?: string
  is_public?: string
}

// The shared client shows a global error dialog. Handle video errors inline instead.
const client = HttpRequest.extend({ hooks: { afterResponse: undefined }, retry: 0, timeout: false })

export class VideoApiError extends Error {
  status: number
  existingId?: string
  constructor(message: string, status: number, existingId?: string) {
    super(message)
    this.status = status
    this.existingId = existingId
  }
}

async function response<T>(request: Promise<Response>): Promise<T> {
  try {
    const res = await request
    const payload = (await res.json()) as ApiResult<T> & { data: { existing_id?: string } }
    if (!res.ok)
      throw new VideoApiError(payload.message || "Không xử lý được yêu cầu.", res.status, payload.data?.existing_id)
    return payload.data as T
  } catch (error) {
    if (error instanceof VideoApiError) throw error
    throw new VideoApiError("Không kết nối được server. Vui lòng thử lại.", 0)
  }
}

export const videoService = {
  async list(params: VideoFilters) {
    const queryParams: Record<string, string | number> = {
      page: params.page,
      limit: params.limit ?? params.per_page ?? 20,
    }
    if (params.search) queryParams["search"] = params.search
    if (params.status) queryParams["status"] = params.status
    if (params.is_public !== undefined && params.is_public !== "") queryParams["is_public"] = params.is_public

    const searchParams = Object.fromEntries(
      Object.entries(queryParams).filter(([, value]) => value !== undefined && value !== "")
    )
    const res = await client.get("v1/videos", { searchParams, throwHttpErrors: false })
    const payload = (await res.json()) as ApiResult<Video[]> & { paginate: { total: number } }
    if (!res.ok) throw new VideoApiError(payload.message, res.status)
    return { videos: payload.data, total: payload.paginate.total }
  },
  detail: (id: string) => response<Video>(client.get(`v1/videos/${id}`, { throwHttpErrors: false })),
  create: (url: string) => response<Video>(client.post("v1/videos", { json: { url }, throwHttpErrors: false })),
  update: (id: string, values: Partial<VideoMetadata> & { status?: VideoStatus }) =>
    response<Video>(client.put(`v1/videos/${id}`, { json: values, throwHttpErrors: false })),
  delete: (id: string) => response<object>(client.delete(`v1/videos/${id}`, { throwHttpErrors: false })),
  subtitles: (id: string) =>
    response<SubtitleResult>(client.get(`v1/videos/${id}/subtitles`, { throwHttpErrors: false })),
  saveTranslations: (id: string, revision: string, translations: { id: number | string; translation: string }[]) =>
    response<SubtitleResult>(
      client.post(`v1/videos/${id}/translations`, { json: { revision, translations }, throwHttpErrors: false })
    ),
  reimport: (id: string) =>
    response<Video>(
      client.post(`v1/videos/${id}/import-subtitles`, {
        json: { replace: true },
        throwHttpErrors: false,
      })
    ),
}
